import React from 'react'

import MascotEars, { type MascotEarAnchor } from './mascot-ears'
import MascotFur from './mascot-fur'
import {
    coatFill,
    MascotEye,
    MascotHeadShading,
    MascotLimbPair,
    MascotPatternLayer,
    MascotPoseFrame,
    MascotSvgRoot,
    MASCOT_PEEK_VIEWBOX,
    type MascotRegion,
    type MascotSvgProps,
} from './mascot-svg-shared'

const HEAD: MascotRegion = { cx: 64, cy: 76, rx: 32, ry: 29, eyeY: 71 }
const EARS: MascotEarAnchor = {
    x: 50,
    y: 62,
    headCx: 64,
    scale: 1.15,
    scaleY: 1.75,
    rotate: 8,
    // A lop at the upright length would hang well past the bottom of the frame.
    scaleByStyle: { droopy: 0.62 },
    oneUpPair: ['upright', 'droopy'],
    innerFill: 'var(--coat-flesh)',
}
const PORTRAIT_VIEWBOX = '-0.3 -0.3 128.6 128.6'
const PEEK_TRANSFORM = 'translate(-5.1 -1.4) scale(1.08)'

/** Tall ears carry the silhouette; head sits low to make room for them. */
export default function RabbitMascot(props: MascotSvgProps) {
    const isPeek = props.pose === 'peek'
    return (
        <MascotSvgRoot
            {...props}
            viewBox={isPeek ? MASCOT_PEEK_VIEWBOX : PORTRAIT_VIEWBOX}
        >
            {ids => (
                <MascotPoseFrame
                    pose={props.pose}
                    ids={ids}
                    peekTransform={PEEK_TRANSFORM}
                    paws={<MascotLimbPair kind="paw" />}
                >
                    <defs>
                        <clipPath id={ids.headClip}>
                            <ellipse
                                cx={HEAD.cx}
                                cy={HEAD.cy}
                                rx={HEAD.rx}
                                ry={HEAD.ry}
                            />
                        </clipPath>
                    </defs>

                    <MascotFur furType={props.furType} region={HEAD} />
                    <MascotEars style={props.earStyle} anchor={EARS} />

                    {/* Head */}
                    <ellipse
                        cx={HEAD.cx}
                        cy={HEAD.cy}
                        rx={HEAD.rx}
                        ry={HEAD.ry}
                        {...coatFill}
                    />
                    <MascotPatternLayer
                        pattern={props.pattern}
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotHeadShading
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotEars
                        style={props.earStyle}
                        anchor={EARS}
                        layer="front"
                    />

                    {/* Cheek fluff */}
                    <ellipse
                        cx="40"
                        cy="88"
                        rx="12"
                        ry="10"
                        fill="var(--coat-secondary)"
                        opacity={0.8}
                    />
                    <ellipse
                        cx="88"
                        cy="88"
                        rx="12"
                        ry="10"
                        fill="var(--coat-secondary)"
                        opacity={0.8}
                    />

                    {/* Nose, mouth, buck teeth */}
                    <path
                        d="M64 88 L58 81 Q64 78 70 81 Z"
                        fill="var(--coat-flesh)"
                        stroke="var(--coat-line)"
                        strokeWidth="1.4"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M64 88 V92 M64 92 C60 96 56 95 54 92 M64 92 C68 96 72 95 74 92"
                        fill="none"
                        stroke="var(--coat-line)"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                    />
                    <path
                        d="M58.5 93 H69.5 A1.5 1.5 0 0 1 71 94.5 V100 A2 2 0 0 1 69 102 H59 A2 2 0 0 1 57 100 V94.5 A1.5 1.5 0 0 1 58.5 93 Z"
                        fill="#fff"
                        stroke="var(--coat-line)"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                    />
                    <line
                        x1="64"
                        y1="94"
                        x2="64"
                        y2="102"
                        stroke="var(--coat-line)"
                        strokeWidth="1.2"
                    />

                    <MascotEye cx={48} cy={71} r={4.8} />
                    <MascotEye cx={80} cy={71} r={4.8} />
                </MascotPoseFrame>
            )}
        </MascotSvgRoot>
    )
}
